import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { NgModule, ModuleWithProviders } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { SharedModule } from 'primeng/api';
import {InputMaskModule} from 'primeng/inputmask';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import {InputSwitchModule} from 'primeng/inputswitch';
import { PaginatorModule } from 'primeng/paginator';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { FileUploadModule } from 'primeng/fileupload';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TabViewModule } from 'primeng/tabview';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';

// this includes the core NgIdleModule but includes keepalive providers for easy wireup
import { NgIdleModule } from '@ng-idle/core';

import { ClipboardModule } from 'ngx-clipboard';

import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';

import { AppComponent } from './app.component';

import { ReadonlyByRoleOnlyDirective } from './shared';

import { routing, appRoutingProviders } from './app.routing';

import { IuiIconectivLibModule, ISecUiConfig, IuiReportingModule } from 'iui-iconectiv-lib';

import { AccountCompletionComponent, AccountCompletionService } from './account-completion';

import { NotApplicablePipe, SearchComponent, SearchModelService, SearchService } from './search';

import { ServiceAgreementComponent, ServiceAgreementService} from './service-agreement';

import { AddDashesPipe, CapitalizePipe, ServiceDeskComponent, ServiceDeskService, AgreementPipe } from './servicedesk';

import { EmailValidatorDirective, RegisterComponent, RegisterService } from './register';

import { SummaryComponent, SummaryService } from './summary';

import {ReportsComponent, ReportsService} from './reports';

import {
  SelectedPipe, TruncatePipe, IsodatePipe,
  NumericOnlyDirective, CommaSeparatedArrayDirective, RequiredDirective,
  RefdataService, WebSocketService, BooleanPipe, RefdataPipe, RangeValidatorDirective, ToWordsPipe,
  CanDeactivateGuard
 } from './shared';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { SupportComponent, PrivacyComponent, LearnMoreComponent } from './support';
import { FeeScheduleComponent } from "./shared/fee-schedule";
import { RecaptchaModule } from 'ng-recaptcha';
import { FeeScheduleService } from './shared/fee-schedule';
import {RegionalUserAgreementComponent} from "./regional-user-agreement";
import {RegionalUserAgreementService} from "./regional-user-agreement/rua.service";
import {CustomFeeScheduleComponent} from "./shared/custom-fee-schedule";
import {AdminAccountComponent} from "./admin-account";
import {TnHistoryComponent} from "./shared/tn-history";
import {UsageDetailsComponent} from "./usage-details";
import {UsageDetailsService} from "./usage-details/usage-details.service";
import {UserManagementComponent} from "./user-management";
import {RolesPipe} from "./user-management/role.pipe";
import {TimeFromNowPipe} from "./user-management/timefromnow.pipe";
import { SortPipe } from './shared/sort.pipe.';
import { AlphaNumOnlyDirective } from './shared/alpha-num-only.directive';
import { ReportsQueueComponent } from './reports/reports-queue.component';
import { DialogModule } from 'primeng/dialog';

declare function require(name: string);

// configuration for the Auth HTTP connections to our server.
export function authHttpServiceFactory(http: HttpClient, options: HttpHeaders) {
  return new JwtModule({
    headerName: 'Authorization',
    headerPrefix: '',
    tokenName: 'isecToken',
    noJwtError: true,
    noTokenScheme: true
  });
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http,"assets/i18n/");
}

export function isecUiFactory() {
  const iSecConfig = new ISecUiConfig();
  iSecConfig.productName = 'TruNumber Finder';
  iSecConfig.inactivityTimeout = 60 * 55;
  iSecConfig.inactivityWarningTimeout = 60 * 5;
  iSecConfig.multipleRolesAllowed = false;
  iSecConfig.singlePageUserAdmin = false;
  iSecConfig.defaultItemsPerPage = 500;
  iSecConfig.requiredProfileFields = ['firstname', 'lastname'];


  return iSecConfig;
}

export function gettoken () {
  return sessionStorage.getItem('isecToken');
}

export const whitelistedDomains = [new RegExp('[\s\S]*')] as RegExp[];
export function jwtOptionsFactory() {
  return {
    skipWhenExpired: false,
    authScheme: '',
    tokenGetter: gettoken,
    whitelistedDomains: whitelistedDomains
  };
}


@NgModule({ declarations: [
        AppComponent,
        RefdataPipe,
        SelectedPipe,
        TruncatePipe,
        IsodatePipe,
        BooleanPipe,
        // TakenPipe,
        ToWordsPipe,
        EmailValidatorDirective,
        RequiredDirective,
        RangeValidatorDirective,
        NumericOnlyDirective,
        ReadonlyByRoleOnlyDirective,
        CommaSeparatedArrayDirective,
        AccountCompletionComponent,
        RegisterComponent,
        SummaryComponent,
        SearchComponent,
        ReportsComponent,
        SupportComponent, LearnMoreComponent, PrivacyComponent,
        ServiceAgreementComponent,
        RegionalUserAgreementComponent,
        ServiceDeskComponent,
        NotApplicablePipe,
        AddDashesPipe,
        AgreementPipe,
        CapitalizePipe,
        RolesPipe,
        AccountCompletionComponent,
        FeeScheduleComponent,
        CustomFeeScheduleComponent,
        AdminAccountComponent,
        TnHistoryComponent,
        UsageDetailsComponent,
        UserManagementComponent,
        TimeFromNowPipe,
        SortPipe,
        AlphaNumOnlyDirective,
        ReportsQueueComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        routing,
        IuiIconectivLibModule,
        CalendarModule,
        MultiSelectModule,
        ConfirmDialogModule,
        DialogModule,
        DropdownModule,
        TabViewModule,
        FileUploadModule,
        ToastModule,
        IuiReportingModule,
        InputMaskModule,
        InputSwitchModule,
        PaginatorModule,
        OverlayPanelModule,
        ClipboardModule,
        TableModule,
        TooltipModule,
        CheckboxModule,
        RadioButtonModule,
        SharedModule,
        NgIdleModule.forRoot(),
        JwtModule.forRoot({ jwtOptionsProvider: { provide: JWT_OPTIONS, useFactory: jwtOptionsFactory } }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        RecaptchaModule], providers: [
        AccountCompletionService,
        RegisterService,
        SearchModelService,
        SearchService,
        ReportsService,
        ServiceAgreementService,
        ServiceDeskService,
        FeeScheduleService,
        SummaryService,
        RegionalUserAgreementService,
        RefdataService,
        UsageDetailsService,
        TruncatePipe,
        TimeFromNowPipe,
        appRoutingProviders,
        {
            provide: ISecUiConfig,
            useFactory: isecUiFactory
        },
        CanDeactivateGuard,
        provideHttpClient(withInterceptorsFromDi())
    ] })

export class AppModule { }
